import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCorners,
  rectIntersection,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { Plus, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { KanbanColumn } from './KanbanColumn.tsx';
import { AddTaskModal } from './AddTaskModal.tsx';
import { DeleteZone } from './DeleteZone.tsx';
import { CategorySelector } from './CategorySelector.tsx';
import { Task, TaskBoardCategories } from './types.ts';
import { getTasks, saveTask } from '../../../utils/tasks.ts'
import { categories as taskCategories } from '../weddingPlan/WeddingPlanSummary.tsx';
import { updateTask, deleteTask } from '../../../utils/tasks.ts'
import { useAgentIntegration } from '../../../hooks/useAgentIntegration.ts';
import { TaskCard } from './TaskCard.tsx';
import confetti from 'canvas-confetti';

const categories = [
  { id: 'all', title: "All", icon: '📋' },
  { id: 'venue', title: 'Venue & Ceremony', icon: '🏰' },
  { id: 'website', title: 'Wedding Website', icon: '💻' },
  { id: 'photo', title: 'Photos & Videos', icon: '📸' },
  { id: 'food', title: 'Food & Drink', icon: '🍽️' },
  { id: 'attire', title: 'Attire', icon: '👗' },
  { id: 'music', title: 'Music', icon: '🎵' },
  { id: 'flowers', title: 'Flowers & Decor', icon: '💐' },
  { id: 'invites', title: 'Invitations & Paper', icon: '✉️' },
  { id: 'planning', title: 'Planning & Admin', icon: '📋' },
  { id: 'honeymoon', title: 'Honeymoon', icon: '✈️' },
];

// Define the Column type
interface Column {
  id: string;
  title: string;
  tasks: Task[];
}

export const KanbanBoard: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDraggingTask, setIsDraggingTask] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  
  // New state for highlighting newly created tasks
  const [newTaskId, setNewTaskId] = useState<number | null>(null);
  const [showSparkles, setShowSparkles] = useState(false);

  // Agent integration
  const { recentActions, refreshTrigger, markActionAsViewed } = useAgentIntegration('task');

  const [columns, setColumns] = useState<Column[]>([
    { id: 'TODO', title: 'To Do', tasks: [] },
    { id: 'IN_PROGRESS', title: 'In Progress', tasks: [] },
    { id: 'DONE', title: 'Done', tasks: [] }
  ]);

  // Configure sensors for better drag experience  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3, // Small distance to prevent accidental drags
      },
    })
  );

  // Check for new task data from chat navigation
  useEffect(() => {
    const checkForNewTask = () => {
      const newTaskData = sessionStorage.getItem('newTaskData');
      if (newTaskData) {
        try {
          const taskData = JSON.parse(newTaskData);
          console.log('🌟 Found new task data from chat:', taskData);
          
          if (taskData.shouldHighlight && taskData.taskId) {
            // Convert taskId to number for consistency
            const taskId = typeof taskData.taskId === 'string' ? parseInt(taskData.taskId) : taskData.taskId;
            console.log('🌟 Setting up highlighting for task ID:', taskId);
            
            setNewTaskId(taskId);
            setShowSparkles(true);
            
            // Trigger localized confetti if requested
            if (taskData.shouldShowConfetti) {
              console.log('🎉 Triggering localized confetti for new task!');
              setTimeout(() => {
                // Try to find the new task card element to position confetti around it
                const taskElement = document.querySelector(`[data-task-id="${taskId}"]`);
                let confettiOrigin = { x: 0.2, y: 0.3 }; // Default position for task board area
                
                if (taskElement) {
                  const rect = taskElement.getBoundingClientRect();
                  const centerX = (rect.left + rect.width / 2) / window.innerWidth;
                  const centerY = (rect.top + rect.height / 2) / window.innerHeight;
                  confettiOrigin = { x: centerX, y: centerY };
                  console.log('🎯 Found task element, positioning confetti at:', confettiOrigin);
                }
                
                // Subtle, localized confetti around the task
                confetti({
                  particleCount: 30,
                  spread: 45,
                  origin: confettiOrigin,
                  colors: ['#FF69B4', '#FF7F50', '#DDA0DD'],
                  startVelocity: 25,
                  gravity: 0.8,
                  scalar: 0.8,
                  ticks: 150,
                  disableForReducedMotion: true
                });
                
                // Small secondary burst slightly offset
                setTimeout(() => {
                  confetti({
                    particleCount: 15,
                    spread: 30,
                    origin: { 
                      x: confettiOrigin.x + 0.05, 
                      y: confettiOrigin.y - 0.03 
                    },
                    colors: ['#FFB6C1', '#FFA07A'],
                    startVelocity: 20,
                    gravity: 0.7,
                    scalar: 0.6,
                    ticks: 100,
                    disableForReducedMotion: true
                  });
                }, 200);
              }, 500); // Slightly longer delay to ensure task is rendered
            }
            
            // Clear the session storage after a delay to ensure task is fetched
            setTimeout(() => {
              sessionStorage.removeItem('newTaskData');
              console.log('🌟 Cleared newTaskData from sessionStorage');
            }, 2000);
            
            // Hide sparkles after 4 seconds (longer to ensure visibility)
            setTimeout(() => {
              console.log('🌟 Hiding sparkles');
              setShowSparkles(false);
            }, 4000);
            
            // Remove highlighting after 8 seconds (longer to ensure user sees it)
            setTimeout(() => {
              console.log('🌟 Removing task highlighting');
              setNewTaskId(null);
            }, 8000);
          }
        } catch (error) {
          console.error('Error parsing new task data:', error);
        }
      }
    };

    // Check immediately on mount
    checkForNewTask();
    
    // Also check periodically for a few seconds in case navigation is delayed
    const interval = setInterval(checkForNewTask, 500);
    
    // Clear interval after 5 seconds
    setTimeout(() => {
      clearInterval(interval);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Memoize task fetching to prevent unnecessary re-renders
  const fetchTasks = useCallback(async () => {
    console.log('📋 Fetching tasks...');
    const tasks = await getTasks();
    if (tasks && tasks.length > 0) {
      console.log('📋 Fetched', tasks.length, 'tasks from API');
      
      let mappedTasks = tasks.map((task: Task) => ({
        id: task.id,
        name: task.name,
        description: task.description,
        priority: task.priority || 'MEDIUM',
        status: task.status || 'TODO',
        category_id: task.taskBoard,
        dueDate: task.dueDate || null,
        createdAt: task.createdAt,
      }));

      if (selectedCategory !== "all") {
        mappedTasks = mappedTasks.filter((task: { category_id: string; }) =>
          task.category_id.toLowerCase().includes(selectedCategory.toLowerCase())
        );
      }

      // Sort tasks: newest first (by createdAt descending), then by orderIndex
      const sortTasks = (tasks: Task[]) => {
        return tasks.sort((a, b) => {
          // If we have a new task to highlight, prioritize it
          if (newTaskId) {
            if (a.id === newTaskId) return -1;
            if (b.id === newTaskId) return 1;
          }
          
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          }
          
          return (a.orderIndex || 0) - (b.orderIndex || 0);
        });
      };

      console.log('📋 Setting columns with sorted tasks');
      setColumns([
        { id: 'TODO', title: 'To Do', tasks: sortTasks(mappedTasks.filter((task: { status: string; }) => task.status === 'TODO')) },
        { id: 'IN_PROGRESS', title: 'In Progress', tasks: sortTasks(mappedTasks.filter((task: { status: string; }) => task.status === 'IN_PROGRESS')) },
        { id: 'DONE', title: 'Done', tasks: sortTasks(mappedTasks.filter((task: { status: string; }) => task.status === 'DONE')) }
      ]);
      
      // Log if we found the highlighted task
      if (newTaskId) {
        const foundTask = mappedTasks.find(task => task.id === newTaskId);
        if (foundTask) {
          console.log('🌟 Found highlighted task in fetched data:', foundTask);
        } else {
          console.log('🌟 Highlighted task not found in fetched data, taskId:', newTaskId);
        }
      }
    } else {
      console.log('📋 No tasks received from API');
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks, refreshTrigger]);

  // Re-fetch tasks when a new task is highlighted to ensure it's included
  useEffect(() => {
    if (newTaskId) {
      console.log('🌟 New task highlighted, re-fetching to ensure it\'s included:', newTaskId);
      // Small delay to ensure the task was saved in the backend
      setTimeout(() => {
        fetchTasks();
      }, 1000);
    }
  }, [newTaskId, fetchTasks]);

  // Handle recent agent actions
  useEffect(() => {
    if (recentActions.length > 0) {
      recentActions.forEach(action => {
        if (action.type === 'task' && !action.executed && action.data && action.data.id) {
          setTimeout(() => {
            markActionAsViewed(action.data!.id);
          }, 3000);
        }
      });
    }
  }, [recentActions, markActionAsViewed]);

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const taskId = active.id;
    
    // Find the task being dragged
    const task = columns.flatMap(col => col.tasks).find(t => t.id.toString() === taskId);
    if (task) {
      setActiveTask(task);
      setIsDraggingTask(true);
      // Add body class for CSS targeting
      document.body.classList.add('dragging');
    }
  }, [columns]);

  // Handle drag over (for visual feedback)
  const handleDragOver = useCallback((event: DragOverEvent) => {
    // @dnd-kit handles column-to-column movement visual feedback automatically
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveTask(null);
    setIsDraggingTask(false);
    // Remove body class
    document.body.classList.remove('dragging');

    if (!over) return;

    const taskId = active.id.toString();
    const overId = over.id.toString();

    // Handle deletion
    if (overId === 'delete-zone') {
      const sourceColumn = columns.find(col => 
        col.tasks.some(task => task.id.toString() === taskId)
      );
      
      if (sourceColumn) {
        const taskToDelete = sourceColumn.tasks.find(task => task.id.toString() === taskId);
        if (taskToDelete) {
          // Immediate visual update
          setColumns(prevColumns => 
            prevColumns.map(col => ({
              ...col,
              tasks: col.tasks.filter(task => task.id.toString() !== taskId)
            }))
          );

          // Async API call
          try {
            await deleteTask(taskToDelete.id);
          } catch (error) {
            console.error('Failed to delete task:', error);
            fetchTasks(); // Revert on error
          }
        }
      }
      return;
    }

    // Find source and destination columns
    const sourceColumn = columns.find(col => 
      col.tasks.some(task => task.id.toString() === taskId)
    );
    
    const destinationColumnId = overId.includes('column-') 
      ? overId.replace('column-', '') 
      : columns.find(col => col.tasks.some(task => task.id.toString() === overId))?.id || overId;

    const destinationColumn = columns.find(col => col.id === destinationColumnId);

    if (!sourceColumn || !destinationColumn) return;

    const task = sourceColumn.tasks.find(t => t.id.toString() === taskId);
    if (!task) return;

    // If moving within the same column, handle reordering
    if (sourceColumn.id === destinationColumn.id) {
      const oldIndex = sourceColumn.tasks.findIndex(t => t.id.toString() === taskId);
      const newIndex = over.id.toString() === destinationColumn.id 
        ? sourceColumn.tasks.length - 1 
        : sourceColumn.tasks.findIndex(t => t.id.toString() === overId);
      
      if (oldIndex !== newIndex) {
        setColumns(prevColumns => 
          prevColumns.map(col => {
            if (col.id === sourceColumn.id) {
              const newTasks = arrayMove(col.tasks, oldIndex, newIndex);
              return { ...col, tasks: newTasks };
            }
            return col;
          })
        );
      }
    } else {
      // Moving between columns
      setColumns(prevColumns => 
        prevColumns.map(col => {
          if (col.id === sourceColumn.id) {
            return { ...col, tasks: col.tasks.filter(t => t.id.toString() !== taskId) };
          } else if (col.id === destinationColumn.id) {
            const newTask = { ...task, status: destinationColumn.id as any };
            return { ...col, tasks: [...col.tasks, newTask] };
          }
          return col;
        })
      );

      // Async API call for column change
      try {
        await updateTask(task.id, { status: destinationColumn.id as any });
      } catch (error) {
        console.error('Failed to update task:', error);
        fetchTasks(); // Revert on error
      }
    }
  }, [columns, fetchTasks]);

  const addTasks = async (taskData: Partial<Task>) => {
    const tasks = await getTasks();
    const lastTaskId = tasks.reduce((max, task) => Math.max(max, task.id), 0);

    const newTask: Task = {
      id: lastTaskId + 1,
      name: taskData.name || '',
      orderIndex: 1,
      description: taskData.description || '',
      priority: taskData.priority || 'MEDIUM',
      dueDate: taskData.dueDate || '',
      status: 'TODO',
      taskBoard: TaskBoardCategories[taskCategories.findIndex(category => 
        category.id === (selectedCategory === 'all' ? taskData.taskBoard : selectedCategory)
      )],
    };

    await saveTask(newTask);
    setColumns(prevColumns => prevColumns.map(col =>
      col.id === 'TODO'
        ? { ...col, tasks: [...col.tasks, newTask] }
        : col
    ));
  };

  const handleAddTask = async (taskData: Partial<Task>) => {
    if (taskData.id) {
      await updateTasks(taskData as Task)
    }
    else {
      await addTasks(taskData);
    }
    setIsModalOpen(false);
  };

  const handleEdit = (task: Task) => {
    setTaskToEdit(task);
    setIsModalOpen(true);
  };

  const updateTasks = async (taskData: Task) => {
    const updatedTask: Partial<Task> = {
      name: taskData.name,
      description: taskData.description || '',
      priority: taskData.priority || 'MEDIUM',
      dueDate: taskData.dueDate || '',
    };
    await updateTask(taskData.id, updatedTask);
    setColumns(prevColumns => prevColumns.map(col => ({
      ...col,
      tasks: col.tasks.map(task => 
        task.id === taskData.id ? { ...task, ...updatedTask } : task
      )
    })));
  };

  // Get all task IDs for SortableContext
  const taskIds = useMemo(() => 
    columns.flatMap(col => col.tasks.map(task => task.id.toString())), 
    [columns]
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-4 w-full max-w-none -mx-4 px-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Task Board</h1>
                <p className="text-gray-600 mt-1">Organize and track your wedding planning tasks</p>
              </div>
              
              {/* New Task Indicator */}
              <AnimatePresence>
                {newTaskId && showSparkles && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    className="flex items-center space-x-2 bg-gradient-to-r from-pink-100 to-rose-100 px-3 py-2 rounded-full border border-pink-200"
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    >
                      <Sparkles className="w-4 h-4 text-pink-600" />
                    </motion.div>
                    <span className="text-sm font-medium text-pink-800">
                      New task added!
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="flex items-center gap-3">
              <CategorySelector
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
              />
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all duration-200 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Task
              </button>
            </div>
          </div>
          
          {/* Delete Zone */}
          <DeleteZone isDraggingTask={isDraggingTask} />

          <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 h-auto sm:h-[75vh] max-h-[600px] w-full overflow-x-auto">
              {columns.map(column => (
                <KanbanColumn
                  key={column.id}
                  column={column}
                  onEdit={handleEdit}
                  newTaskId={newTaskId}
                  showSparkles={showSparkles}
                />
              ))}
            </div>
          </SortableContext>

          {/* Drag Overlay */}
          <DragOverlay 
            dropAnimation={null}
          >
            {activeTask ? (
              <div style={{ 
                transform: 'rotate(3deg)', 
                opacity: 0.95,
                cursor: 'grabbing'
              }}>
                <TaskCard
                  task={activeTask}
                  index={0}
                  onEdit={() => {}}
                  isDragging={true}
                  isOverlay={true}
                />
              </div>
            ) : null}
          </DragOverlay>
        </div>
      </DndContext>
      
      <AddTaskModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setTaskToEdit(null);
        }}
        onAddTask={handleAddTask}
        editTask={taskToEdit}
        selectedCategory={selectedCategory}
        showCategoryDropdown={selectedCategory === 'all'}
      />
    </>
  );
};